import React, {useEffect, useMemo} from "react"
import {BackButton} from "../../components/BackButton/BackButton"
import {useHistory, useLocation} from "react-router-dom"
import {useFormik} from "formik"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import * as yup from "yup"
import {Helmet} from "react-helmet-async"
import {Button, Checkbox, Form, Tag} from "antd"
import {FormInput} from "../../components/Form/FormInput"
import {getCompanies, setCompanies} from "../../store/companyReducer"
import {FormSelect} from "../../components/Form/FormSelect"
import {getUserGroups, setUserGroups} from "../../store/userGroupReducer"
import {ID_SYSTEM_COMPANY} from "../../constants/constants"
import {changeUser, createUser} from "../../store/userReducer"
import {getUserRoles, setUserRoles} from "../../store/appReducer"
import {UserResponse} from "../../models/response"

export const UserForm = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation<{ user: UserResponse, idCompany?: number, idUserGroup?: number }>()
    const user = location?.state?.user
    const idCompany = location?.state?.idCompany
    const idUserGroup = location?.state?.idUserGroup
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const status = useSelector((state: StateType) => state.appReducer.status)
    const companies = useSelector((state: StateType) => state.companyReducer.companies)
    const userGroups = useSelector((state: StateType) => state.userGroupReducer.userGroups)
    const userRoles = useSelector((state: StateType) => state.appReducer.userRoles)
    const controller = useMemo(() => new AbortController(), [])

    const schema = useMemo(() => {
        if (user) return yup.object().shape({
            name: yup.string().required('Введите ФИО').max(250, 'Максимальная длина 250 символов'),
            idUserGroup: yup.number().required('Выберите группу').min(1, 'Выберите группу'),
            idUserType: yup.number().required('Выберите роль').min(1, 'Выберите роль'),
            phone: yup.string().max(50, 'Максимальная длина 50 символов'),
            telegramId: yup.string().max(50, 'Максимальная длина 50 символов'),
        })
        else return  yup.object().shape({
            name: yup.string().required('Введите ФИО').max(250, 'Максимальная длина 250 символов'),
            idUserGroup: yup.number().required('Выберите группу').min(1, 'Выберите группу'),
            idUserType: yup.number().required('Выберите роль').min(1, 'Выберите роль'),
            phone: yup.string().max(50, 'Максимальная длина 50 символов'),
            telegramId: yup.string().max(50, 'Максимальная длина 50 символов'),
            email: yup.string().trim().email('Это не email').required('Введите email').max(100, 'Максимальная длина 100 символов'),
            password: yup.string().required('Введите пароль'),
        })
    }, [user])

    useEffect(() => {
        dispatch(getCompanies({controller}))

        return () => {
            controller.abort()
            dispatch(setCompanies(null))
            dispatch(setUserGroups(null))
            dispatch(setUserRoles(null))
        }
    }, [dispatch, controller])

    const formik = useFormik({
        initialValues: {
            name: user?.name || '',
            idCompany: user?.idCompany || idCompany,
            idUserGroup: user?.idUserGroup || idUserGroup,
            idUserType: user?.idUserType,
            phone: user?.phone || '',
            telegramId: user?.telegramId || '',
            email: user?.email || '',
            password: '',
            active: user ? user.active : true,
            notify: user ? user.notify : 2,
        },
        validationSchema: schema,
        onSubmit: async (values) => {
            let res: any
            if (user) {
                res = await dispatch(changeUser({
                    id: user.id,
                    name: values.name,
                    idUserGroup: values.idUserGroup || 0,
                    idUserType: values.idUserType,
                    phone: values.phone,
                    telegramId: values.telegramId,
                    active: values.active,
                    controller,
                }))
            } else {
                res = await dispatch(createUser({
                    name: values.name,
                    idUserGroup: values.idUserGroup || 0,
                    idUserType: values.idUserType,
                    phone: values.phone,
                    telegramId: values.telegramId,
                    email: values.email,
                    password: values.password,
                    active: values.active,
                    controller,
                }))
            }
            if (res?.payload?.status === 0) {
                if (!user)
                {
                    history.replace(`/user/${res.payload.data.id}`)
                }
                else
                {
                    history.goBack()
                }
            }
        },
    })
    const {values, handleChange, handleBlur, handleSubmit, errors, touched, setFieldValue} = formik

    useEffect(() => {
        if (values?.idCompany) {
            dispatch(getUserGroups({idCompany: values.idCompany, controller}))
            dispatch(getUserRoles({isSystem: values.idCompany === ID_SYSTEM_COMPANY ? 1 : 0, controller}))
        }
    }, [dispatch, values?.idCompany, controller])

    useEffect(() => {
        if (userGroups?.data && !values?.idUserGroup) {
            setFieldValue('idUserGroup', userGroups.data[0]?.id)
        }
    }, [userGroups, values?.idUserGroup])

    useEffect(() => {
        if (companies?.data && !values?.idCompany) {
            setFieldValue('idCompany', companies.data[0]?.id)
        }

        if (userGroups?.data && !values?.idUserGroup) {
            setFieldValue('idUserGroup', userGroups.data[0]?.id)
        }

        if (userRoles?.data && !values?.idUserType) {
            setFieldValue('idUserType', userRoles.data[0]?.id)
        }
    }, [companies, userGroups, userRoles, values])


    const companyOptions = useMemo(() =>
        companies?.data
            ?.filter(i => user ? user.idCompany === ID_SYSTEM_COMPANY ? i.id === ID_SYSTEM_COMPANY : i.id !== ID_SYSTEM_COMPANY : true)
            ?.map(i => ({value: i.id, label: i.name})) || [], [companies, user])

    const userGroupOptions = useMemo(() => userGroups?.data?.map(i => ({value: i.id, label: i.name})) || [], [userGroups])
    const userRoleOptions = useMemo(() => userRoles?.data?.map(i => ({value: i.id, label: i.name})) || [], [userRoles])

    return (
        <div className="form-page">
            <Helmet>
                <title>{user ? 'Обновление пользователя' : 'Новый пользователь'}</title>
            </Helmet>
            <Form className="form-container"
                  autoComplete="off"
                  onSubmitCapture={handleSubmit}
            >
                <div className="description-header"><BackButton onClick={() => history.goBack()}/></div>
                <div className="description-title">{user ? 'Обновление пользователя' : 'Новый пользователь'}</div>
                {
                    profile?.idCompany === ID_SYSTEM_COMPANY
                        ? <FormSelect placeholder="Компания"
                                      value={values.idCompany || ''}
                                      onChange={(value) => {
                                          dispatch(setUserGroups(null))
                                          dispatch(setUserRoles(null))
                                          setFieldValue('idCompany', value)
                                          setFieldValue('idUserGroup', null)
                                          setFieldValue('idUserType', null)
                                      }}
                                      options={companyOptions}
                                      className="form-select"
                                      isInvalid={touched.idCompany && errors.idCompany}
                        />
                        : null
                }
                <FormSelect placeholder="Группа"
                            value={values.idUserGroup || ''}
                            onChange={(value) => setFieldValue('idUserGroup', value)}
                            options={userGroupOptions}
                            className="form-select"
                            isInvalid={touched.idUserGroup && errors.idUserGroup}
                />
                <FormSelect placeholder="Роль"
                            value={values.idUserType || ''}
                            onChange={(value) => setFieldValue('idUserType', value)}
                            options={userRoleOptions}
                            className="form-select"
                            isInvalid={touched.idUserType && errors.idUserType}
                />
                <FormInput placeholder="ФИО"
                           value={values.name}
                           name="name"
                           onBlur={handleBlur}
                           onChange={handleChange}
                           isInvalid={touched.name && errors.name}
                           allowClear
                />
                {
                    !user
                        ? <>
                            <FormInput placeholder="Email"
                                       value={values.email}
                                       name="email"
                                       onBlur={handleBlur}
                                       onChange={handleChange}
                                       isInvalid={touched.email && errors.email}
                                       allowClear
                            />
                            <FormInput placeholder="Пароль"
                                       value={values.password}
                                       name="password"
                                       onBlur={handleBlur}
                                       onChange={handleChange}
                                       isInvalid={touched.password && errors.password}
                                       allowClear
                                       isPassword
                            />
                        </>
                        : null
                }
                <FormInput placeholder="Телефон"
                           value={values.phone}
                           name="phone"
                           onBlur={handleBlur}
                           onChange={handleChange}
                           isInvalid={touched.phone && errors.phone}
                           allowClear
                />
                <FormInput placeholder="Telegram ID"
                           value={values.telegramId}
                           name="telegramId"
                           onBlur={handleBlur}
                           onChange={handleChange}
                           isInvalid={touched.telegramId && errors.telegramId}
                           allowClear
                />

                <div style={{marginTop: 20}}>
                    <Checkbox checked={values.active}
                              name="active"
                              onChange={handleChange}
                    >
                        <Tag color={values.active ? 'green' : 'default'}>Активно</Tag>
                    </Checkbox>
                </div>

                <div className="form-button-block">
                    <Button type="primary"
                            htmlType="submit"
                            loading={status === 'loading'}
                    >
                        Сохранить
                    </Button>
                    <Button type="default"
                            onClick={() => history.goBack()}
                    >
                        Отмена
                    </Button>
                </div>
            </Form>
        </div>
    )
}

